import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

// Token yenileme aralığı (dakika)
const TOKEN_REFRESH_INTERVAL = 60 * 60 * 1000; // 60 dakika (auto-logout pasif)

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('erp_token'));
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('erp_user');
    return u ? JSON.parse(u) : null;
  });
  const [company, setCompany] = useState(() => {
    const c = localStorage.getItem('erp_company');
    return c ? JSON.parse(c) : null;
  });
  const [role, setRole] = useState(() => localStorage.getItem('erp_role'));
  const [permissions, setPermissions] = useState(() => {
    const p = localStorage.getItem('erp_permissions');
    return p ? JSON.parse(p) : {};
  });
  const [customPermissions, setCustomPermissions] = useState(() => {
    const cp = localStorage.getItem('erp_custom_permissions');
    return cp ? JSON.parse(cp) : null;
  });
  const [costCenters, setCostCenters] = useState([]);
  const [companies, setCompanies] = useState(() => {
    const c = localStorage.getItem('erp_companies');
    return c ? JSON.parse(c) : [];
  });
  
  const refreshIntervalRef = useRef(null);

  // EFFECT'LER SIRKET NESNESINE DEGIL KIMLIGINE BAGLANIR (2026-09-28).
  // `/auth/me` her yanitta sirketi YENI bir nesne olarak donduruyor ve asagidaki
  // effect onu `setCompany` ile yaziyordu; `company` ayni effect'in bagimliligi
  // oldugu icin her yanit effect'i yeniden tetikliyordu -- SONSUZ DONGU. Olctuk:
  // acik her sayfada 6 saniyede 510 `/auth/me` (~85 istek/sn), oturumu olan
  // herkeste. Ayni kimlik degisimi 60 dakikalik token yenileme zamanlayicisini
  // da her yanitta sifirliyordu, yani yenileme HIC calismiyordu.
  // Sirket degisimi zaten TAM SAYFA YENILEMEYLE yapiliyor (ERP.md "Sirket
  // secimi"), kimlige baglamak hicbir gecisi kacirmaz.
  const sirketId = company?.id;

  // Token yenileme fonksiyonu
  const refreshToken = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.post('/auth/refresh-token');
      const newToken = res.data.token;
      localStorage.setItem('erp_token', newToken);
      setToken(newToken);
    } catch (err) {
      // Auto-logout pasif — token yenilenemese bile oturumu kapat
      // if (err.response?.status === 401) { logout(); }
      console.warn('Token yenileme başarısız, oturum devam ediyor:', err?.response?.status);
    }
  }, [token]);

  // Sliding window için token yenileme interval'ı
  useEffect(() => {
    if (token && sirketId) {
      // 60 dakikada bir yenile (TOKEN_REFRESH_INTERVAL); token 24 saatlik, pencere kayar
      refreshIntervalRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [token, sirketId, refreshToken]);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token: t, user: u, companies: c } = res.data;
    localStorage.setItem('erp_token', t);
    localStorage.setItem('erp_user', JSON.stringify(u));
    localStorage.setItem('erp_companies', JSON.stringify(c));
    localStorage.removeItem('erp_company');
    localStorage.removeItem('erp_role');
    localStorage.removeItem('erp_permissions');
    setToken(t);
    setUser(u);
    setCompanies(c);
    setCompany(null);
    setRole(null);
    setPermissions({});

    // Geçici şifre gerektiriyorsa otomatik şirket seçimi yapma
    if (u?.is_temporary_password) return { companies: c, user: u };

    // Birden fazla şirkete erişimi olan kullanıcıya HER GİRİŞTE sorulur.
    // Bir dönem son seçilen şirket hatırlanıp otomatik seçiliyordu; şirket
    // seçimi atlandığı için kullanıcı hangi şirkette olduğunu görmeden
    // çalışmaya başlıyordu — yanlış şirkete kayıt riski. Tek şirketi olanda
    // soru anlamsız, o otomatik geçer.
    const autoSelect = c.length === 1 ? c[0].id : null;

    if (autoSelect) {
      try {
        const selRes = await api.post('/auth/select-company', { company_id: autoSelect });
        const { token: st, company: sc, role: sr, cost_centers: scc, permissions: sp, custom_permissions: scp } = selRes.data;
        localStorage.setItem('erp_token', st);
        localStorage.setItem('erp_company', JSON.stringify(sc));
        localStorage.setItem('erp_role', sr);
        localStorage.setItem('erp_permissions', JSON.stringify(sp || {}));
        if (scp) localStorage.setItem('erp_custom_permissions', JSON.stringify(scp));
        else localStorage.removeItem('erp_custom_permissions');
        setToken(st);
        setCompany(sc);
        setRole(sr);
        setCostCenters(scc || []);
        setPermissions(sp || {});
        setCustomPermissions(scp || null);
        return { companies: c, user: u, autoSelectedCompany: sc };
      } catch (_) {
        // Otomatik seçim başarısız olursa kullanıcı manuel seçer
      }
    }

    return { companies: c, user: u };
  }, []);

  const selectCompany = useCallback(async (companyId) => {
    const res = await api.post('/auth/select-company', { company_id: companyId });
    const { token: t, company: c, role: r, cost_centers: cc, permissions: p, custom_permissions: cp } = res.data;
    localStorage.setItem('erp_token', t);
    localStorage.setItem('erp_company', JSON.stringify(c));
    localStorage.setItem('erp_role', r);
    localStorage.setItem('erp_permissions', JSON.stringify(p || {}));
    if (cp) {
      localStorage.setItem('erp_custom_permissions', JSON.stringify(cp));
    } else {
      localStorage.removeItem('erp_custom_permissions');
    }
    setToken(t);
    setCompany(c);
    setRole(r);
    setCostCenters(cc || []);
    setPermissions(p || {});
    setCustomPermissions(cp || null);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      // Ignore logout errors
    }
    
    localStorage.removeItem('erp_token');
    localStorage.removeItem('erp_company');
    localStorage.removeItem('erp_user');
    localStorage.removeItem('erp_role');
    localStorage.removeItem('erp_permissions');
    localStorage.removeItem('erp_custom_permissions');
    localStorage.removeItem('erp_companies');
    setToken(null);
    setUser(null);
    setCompany(null);
    setRole(null);
    setPermissions({});
    setCustomPermissions(null);
    setCostCenters([]);
    setCompanies([]);
    
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    // Geçici şifre flag'ini güncelle
    if (user) {
      const updatedUser = { ...user, is_temporary_password: false };
      localStorage.setItem('erp_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    }
    return res.data;
  }, [user]);

  // Yetki kontrolü helper fonksiyonu
  // Önce customPermissions kontrol edilir, yoksa rol şablonu (permissions) kullanılır
  const hasPermission = useCallback((page, action = 'view') => {
    // super_admin her şeye erişebilir
    if (permissions?.all && permissions.all.includes('*')) return true;
    
    // Rol şablonu TABAN, kişisel yetkiler yalnızca kendi sayfasında EZER.
    //
    // Eskiden custom_permissions varsa rol şablonuna hiç bakılmıyordu; rol
    // atanırken şablonun kopyası kullanıcıya yazıldığı için yetkiler o ana
    // donuyordu. Şablona sonradan eklenen sayfa (ör. Mutfak Şef → "recipes")
    // menüde hiç görünmüyordu. Sunucu tarafındaki `get_user_permissions` ile
    // AYNI kural.
    const sayfaYetkisi = (customPermissions && customPermissions[page])
      || (permissions && permissions[page])
      || [];
    return sayfaYetkisi.includes(action) || sayfaYetkisi.includes('*');
  }, [permissions, customPermissions]);

  // Sayfa yetkisi kontrolü
  const canAccessPage = useCallback((path) => {
    // super_admin her şeye erişebilir
    if (permissions?.all && permissions.all.includes('*')) return true;
    
    // Sayfa-yetki eşleştirmesi (alt sayfa bazlı)
    const pageMap = {
      '/erp/products': 'products',
      '/erp/warehouses': 'warehouses',
      '/erp/stock/movements': 'stock_movements',
      '/erp/stock/counts': 'stock_counts',
      '/erp/stock/waste': 'waste',
      '/erp/purchasing': 'purchasing',
      '/erp/recipes': 'recipes',
      '/erp/production-orders': 'production_orders',
      '/erp/sales': 'sales',
      '/erp/treasury': 'treasury',
      '/erp/suppliers': 'suppliers',
      '/erp/invoices': 'invoices',
      '/erp/payment-calendar': 'payment_calendar',
      '/erp/hr/employees': 'employees',
      '/erp/hr/timesheet': 'timesheet',
      '/erp/admin': 'admin',
      '/erp/pos-integration': 'pos_integration',
    };
    
    // En uygun eşleşmeyi bul (uzun path'ler önce kontrol edilsin)
    const sortedPaths = Object.keys(pageMap).sort((a, b) => b.length - a.length);
    for (const pagePath of sortedPaths) {
      if (path.startsWith(pagePath)) {
        return hasPermission(pageMap[pagePath], 'view');
      }
    }
    
    // Dashboard her zaman erişilebilir veya dashboard yetkisi kontrol et
    if (path === '/erp/dashboard' || path === '/erp') {
      return hasPermission('dashboard', 'view') || true; // Dashboard varsayılan olarak açık
    }
    
    return false;
  }, [permissions, hasPermission]);

  useEffect(() => {
    if (token && sirketId) {
      api.get('/auth/me').then(res => {
        setUser(res.data.user);
        setCostCenters(res.data.cost_centers || []);
        if (res.data.company) setCompany(res.data.company);
        if (res.data.role) setRole(res.data.role);
        if (res.data.permissions) {
          setPermissions(res.data.permissions);
          localStorage.setItem('erp_permissions', JSON.stringify(res.data.permissions));
        }
        if (res.data.custom_permissions) {
          setCustomPermissions(res.data.custom_permissions);
          localStorage.setItem('erp_custom_permissions', JSON.stringify(res.data.custom_permissions));
        }
      }).catch(() => {});
    }
  }, [token, sirketId]);

  // If we have token but no company and no companies list, re-fetch companies
  useEffect(() => {
    if (token && !company && companies.length === 0) {
      api.get('/auth/me').then(res => {
        const u = res.data.user;
        setUser(u);
        if (u?.company_access?.length > 0) {
          // Fetch companies list using a dedicated endpoint
          api.get('/auth/companies').then(r => {
            setCompanies(r.data);
            localStorage.setItem('erp_companies', JSON.stringify(r.data));
          }).catch(() => {});
        }
      }).catch(() => {
        // Token invalid, clear everything
        logout();
      });
    }
  }, [token, company, companies.length, logout]);

  return (
    <AuthContext.Provider value={{
      token, user, company, role, permissions, customPermissions, costCenters, companies,
      login, selectCompany, logout, changePassword,
      hasPermission, canAccessPage
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
